/**
 * DialogChrome.tsx — the pieces that make a Prospera admin edit modal read
 * like the corporate dashboard's (the team's reference design):
 *
 *   - fieldClasses: corp's lib/field-styles.ts (error / warning / none states
 *     for a field's wrapper, label, input, hint and message), same Tailwind;
 *   - DialogField: a label (corp Label: text-sm font-medium leading-none) with
 *     an inline "— required" style hint and an optional "modified" pill, the
 *     control, then a muted description line;
 *   - SwitchField: corp's Switch + Label row with the description under it;
 *   - CheckboxList: a column of checkbox rows (facilities, categories), with
 *     All / None buttons like corp's facility list;
 *   - DialogHeading: title (blue on edit, green on clone) + description;
 *   - ModifiedPill: corp's blue "modified" pill with its revert button, beside
 *     a field changed since the form loaded;
 *   - RequiredFieldsPanel / SuggestionsPanel: corp's DialogRequiredFields and
 *     DialogSuggestions strips, rendered at the top of the modal footer.
 *
 * Prospera's own Modal / Switch / Button are used underneath; nothing here is
 * imported from the corporate dashboard.
 *
 * Inside a locked FieldLockScope (a Locked Admin page's form, see
 * components/ui/FieldLock.tsx) the fields read as plain values: SwitchField
 * and CheckboxList don't change and aren't greyed, CheckboxList drops All /
 * None, SelectedRows drops its remove buttons and ModifiedPill renders nothing.
 */
import type { ReactNode } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { Switch } from '@/components/ui/Switch';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';

// ─── Field states (corp lib/field-styles.ts) ───────────────────────────────────

export type FieldState = 'error' | 'warning' | 'none';

export const fieldClasses = {
  wrapper: (s: FieldState) =>
    s === 'error'
      ? 'rounded-md border border-red-300 bg-red-50/50 p-2'
      : s === 'warning'
        ? 'rounded-md border border-amber-300 bg-amber-50/30 p-2'
        : '',
  label: (s: FieldState) =>
    s === 'error' ? 'text-red-700 font-semibold' : s === 'warning' ? 'text-amber-600 font-semibold' : '',
  input: (s: FieldState) =>
    s === 'error'
      ? 'border-red-400 ring-1 ring-red-200'
      : s === 'warning'
        ? 'border-amber-400 ring-1 ring-amber-200'
        : '',
  hint: (s: FieldState) =>
    s === 'error' ? 'text-red-500 font-normal' : s === 'warning' ? 'text-amber-600 font-normal' : '',
  message: (s: FieldState) =>
    s === 'error'
      ? 'text-xs text-red-500 font-medium'
      : s === 'warning'
        ? 'text-xs text-amber-600 font-medium'
        : '',
  icon: (s: FieldState) => (s === 'error' ? 'text-red-400' : s === 'warning' ? 'text-amber-400' : 'text-slate-400'),
  /** Corp's fieldDashedBorderClass — a SOLID border despite the name (no dotted lines in Prospera). */
  dashed: (s: FieldState) => (s === 'error' ? 'border-red-300' : s === 'warning' ? 'border-amber-300' : ''),
};

/** Corp's Label: small, medium weight, no extra line height. */
export const LABEL_CLASS = 'flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm font-medium leading-none text-slate-900 select-none';

/** Corp's muted description line under a field. */
export const DESCRIPTION_CLASS = 'text-xs text-slate-500';

// ─── Field ─────────────────────────────────────────────────────────────────────

export interface DialogFieldProps {
  label: ReactNode;
  htmlFor?: string;
  /** Adds " *" after the label (cosmetic only — validate in the save handler). */
  required?: boolean;
  state?: FieldState;
  /** Inline after the label, e.g. "— required" (styled by `state`). */
  labelHint?: ReactNode;
  /** The "modified" pill, inline after the label. */
  badge?: ReactNode;
  /** A message above the label (corp puts warnings there). */
  topMessage?: ReactNode;
  /** Muted line under the control. */
  description?: ReactNode;
  /** An error line under the control (replaces nothing; shown in red). */
  error?: string;
  className?: string;
  children: ReactNode;
}

export function DialogField({
  label,
  htmlFor,
  required: requiredProp,
  state: stateProp = 'none',
  labelHint: labelHintProp,
  badge,
  topMessage: topMessageProp,
  description,
  error: errorProp,
  className,
  children,
}: DialogFieldProps) {
  // Inside a locked scope the field only shows its saved value: no required
  // marker, hint, warning or error, since staff cannot act on them (R18).
  const locked = useFieldLocked();
  const required = locked ? false : requiredProp;
  const state = locked ? 'none' : stateProp;
  const labelHint = locked ? undefined : labelHintProp;
  const topMessage = locked ? undefined : topMessageProp;
  const error = locked ? undefined : errorProp;
  return (
    // grid-cols-1 (a minmax(0, 1fr) column), not the default auto column: an
    // auto column grows to the widest content, so a closed select holding a
    // long name widened the field past its box and over the next field.
    // content-start: in a row, a field is stretched to its tallest neighbour
    // (one with a description under it), and a stretched grid hands the extra
    // height to its rows — the label grew and pushed the control down, out of
    // line with the controls beside it.
    <div className={cn('grid grid-cols-1 content-start gap-2', fieldClasses.wrapper(state), className)}>
      {topMessage &&<p className={cn(fieldClasses.message(state) || DESCRIPTION_CLASS, 'flex items-center gap-1')}>{topMessage}</p>}
      {/* The pill holds a button, so it sits beside the label, not inside it. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <label htmlFor={htmlFor} className={cn(LABEL_CLASS, fieldClasses.label(state))}>
          <span>
            {label}
            {required && ' *'}
            {labelHint && <span className={fieldClasses.hint(state)}> {labelHint}</span>}
          </span>
        </label>
        {badge}
      </div>
      {children}
      {error && (
        <p role="alert" className="text-xs font-medium text-red-500">
          {error}
        </p>
      )}
      {description && <p className={DESCRIPTION_CLASS}>{description}</p>}
    </div>
  );
}

// ─── Switch row ────────────────────────────────────────────────────────────────

export interface SwitchFieldProps {
  id: string;
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
  /** Accessible name when `label` is not plain text. */
  ariaLabel?: string;
  className?: string;
}

export function SwitchField({ id, label, checked, onChange, description, badge, disabled, ariaLabel, className }: SwitchFieldProps) {
  const locked = useFieldLocked();
  return (
    <div className={cn('grid gap-1', className)}>
      <div className="flex items-center gap-2">
        <Switch id={id} size="md" checked={checked} onChange={onChange} disabled={disabled} aria-label={ariaLabel} />
        <label
          htmlFor={id}
          className={cn(LABEL_CLASS, locked ? 'cursor-default' : disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer')}
        >
          <span>{label}</span>
        </label>
        {badge}
      </div>
      {description && <p className={DESCRIPTION_CLASS}>{description}</p>}
    </div>
  );
}

// ─── Checkbox list (facilities, categories) ──────────────────────────────────────

export interface CheckboxListItem {
  id: number;
  label: string;
}

export interface CheckboxListProps {
  /** Prefix for each checkbox id: `${idPrefix}-${item.id}`. */
  idPrefix: string;
  items: CheckboxListItem[];
  selected: number[];
  onChange: (ids: number[]) => void;
  /** Show the All / None buttons (default true). */
  allNone?: boolean;
  allLabel?: string;
  noneLabel?: string;
  /** Text rows: 'xs' (corp facility list) or 'sm' (corp category list). */
  textSize?: 'xs' | 'sm';
  className?: string;
  listClassName?: string;
  disabled?: boolean;
}

export function CheckboxList({
  idPrefix,
  items,
  selected,
  onChange,
  allNone = true,
  allLabel = 'All',
  noneLabel = 'None',
  textSize = 'xs',
  className,
  listClassName,
  disabled,
}: CheckboxListProps) {
  const locked = useFieldLocked();
  function toggle(id: number, on: boolean) {
    onChange(on ? (selected.includes(id) ? selected : [...selected, id]) : selected.filter((x) => x !== id));
  }
  return (
    <div className={cn('space-y-1.5', className)}>
      {allNone && !locked && items.length > 0 && (
        <div className="mb-1 flex gap-1">
          <Button variant="secondary" size="xs" className="h-7 px-2" disabled={disabled} onClick={() => onChange(items.map((i) => i.id))}>
            {allLabel}
          </Button>
          <Button variant="secondary" size="xs" className="h-7 px-2" disabled={disabled} onClick={() => onChange([])}>
            {noneLabel}
          </Button>
        </div>
      )}
      <div className={cn('space-y-1.5', listClassName)}>
        {items.map((item) => {
          const id = `${idPrefix}-${item.id}`;
          return (
            <div key={item.id} className="flex items-center gap-2 py-0.5">
              <Checkbox
                id={id}
                checked={selected.includes(item.id)}
                onChange={(e) => toggle(item.id, e.target.checked)}
                disabled={disabled}
                wrapperClassName="items-center gap-2"
              />
              <label
                htmlFor={id}
                className={cn(
                  'font-medium leading-none text-slate-900',
                  textSize === 'xs' ? 'text-xs' : 'text-sm',
                  locked ? 'cursor-default' : disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                )}
              >
                {item.label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Heading ───────────────────────────────────────────────────────────────────

const TITLE_VARIANT = {
  default: 'text-slate-900',
  edit: 'text-blue-600',
  clone: 'text-green-600',
} as const;

export function DialogHeading({
  title,
  description,
  variant = 'default',
}: {
  title: ReactNode;
  description?: ReactNode;
  variant?: keyof typeof TITLE_VARIANT;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className={cn('text-lg font-semibold leading-none', TITLE_VARIANT[variant])}>{title}</h2>
      {description && <p className="text-sm text-slate-500">{description}</p>}
    </div>
  );
}

// ─── "modified" pill ─────────────────────────────────────────────────────────────

/** Corp's modifiedBadge: beside a field's label while it differs from the loaded value. Never on a locked field. */
export function ModifiedPill({ label, onRevert }: { label: string; onRevert: () => void }) {
  const locked = useFieldLocked();
  if (locked) return null;
  return (
    <span
      data-testid="modified-pill"
      className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-blue-700"
    >
      modified
      <button
        type="button"
        className="hover:text-blue-900"
        aria-label={`Revert ${label}`}
        title="Revert"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRevert();
        }}
      >
        <RotateCcw className="h-2.5 w-2.5" aria-hidden />
      </button>
    </span>
  );
}

/** Selected ids as removable rows under a multi-select (corp's chip list). */
export function SelectedRows<K extends number | string = number>({
  rows,
  onRemove,
  removeLabel,
}: {
  rows: { id: K; label: string; unavailable?: boolean }[];
  onRemove: (id: K) => void;
  removeLabel: string;
}) {
  const locked = useFieldLocked();
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs">
          <span className="min-w-0 flex-1 truncate">{r.label}</span>
          {r.unavailable && <span className="shrink-0 text-[10px] text-amber-700">unavailable</span>}
          {!locked && (
            <button
              type="button"
              aria-label={`${removeLabel} ${r.label}`}
              className="shrink-0 text-slate-400 hover:text-red-600"
              onClick={() => onRemove(r.id)}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Required / suggestion strips ──────────────────────────────────────────────

function Strip({ tone, heading, items }: { tone: 'red' | 'amber'; heading: string; items: string[] }) {
  return (
    <div
      data-testid={tone === 'red' ? 'required-fields' : 'suggestions'}
      className={cn(
        'space-y-1 border-b px-4 py-3 text-sm md:px-6',
        tone === 'red' ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700',
      )}
    >
      <p className="font-medium">{heading}</p>
      <ul className="list-disc space-y-0.5 pl-5">
        {items.map((msg) => (
          <li key={msg}>{msg}</li>
        ))}
      </ul>
    </div>
  );
}

export function RequiredFieldsPanel({ errors, isCreating = false }: { errors: string[]; isCreating?: boolean }) {
  if (errors.length === 0) return null;
  return <Strip tone="red" heading={`Required before ${isCreating ? 'creating' : 'saving'}:`} items={errors} />;
}

export function SuggestionsPanel({ suggestions, isCreating = false }: { suggestions: string[]; isCreating?: boolean }) {
  if (suggestions.length === 0) return null;
  return <Strip tone="amber" heading={`Suggestions before ${isCreating ? 'creating' : 'saving'}:`} items={suggestions} />;
}

/**
 * A modal footer laid out like corp's: the required / suggestion strips full
 * width across the top (pulled out to the footer's edges), then the row.
 */
export function DialogFooterLayout({
  errors = [],
  suggestions = [],
  isCreating = false,
  children,
}: {
  errors?: string[];
  suggestions?: string[];
  isCreating?: boolean;
  children: ReactNode;
}) {
  const strips = errors.length > 0 || suggestions.length > 0;
  return (
    <div className="flex w-full flex-col">
      {strips && (
        <div className="-mx-4 -mt-4 mb-4 md:-mx-6">
          <RequiredFieldsPanel errors={errors} isCreating={isCreating} />
          <SuggestionsPanel suggestions={suggestions} isCreating={isCreating} />
        </div>
      )}
      <div className="flex w-full flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}
